import { RefreshCw } from "lucide-react";
import { Link } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import type { ChatMessage } from "@/integrations/backend/chat";
import { cn } from "@/lib/utils";
import { ChatComposer } from "@/components/client/ChatComposer";
import { ChatMessageList } from "@/components/client/ChatMessageList";
import { TrainerChatSidebar, type TrainerInfo } from "@/components/client/TrainerChatSidebar";

export type ClientChatShellProps = {
  trainer: TrainerInfo | null;
  trainerId: string | null;
  trainerResolved: boolean;
  messages: ChatMessage[];
  loading: boolean;
  refreshing: boolean;
  sending: boolean;
  draft: string;
  onDraftChange: (value: string) => void;
  onSend: () => void;
  onRefresh: () => void;
  listRef?: React.RefObject<HTMLDivElement | null>;
};

export function ClientChatShell({
  trainer,
  trainerId,
  trainerResolved,
  messages,
  loading,
  refreshing,
  sending,
  draft,
  onDraftChange,
  onSend,
  onRefresh,
  listRef,
}: ClientChatShellProps) {
  const composerDisabled = !trainerResolved || !trainerId;

  return (
    <div className="flex flex-1 flex-col min-h-0 w-full max-w-6xl mx-auto xl:max-w-none">
      <div className="flex flex-1 min-h-0 gap-4 xl:grid xl:grid-cols-[minmax(0,1fr)_320px]">
        <div
          className={cn(
            "flex flex-col flex-1 min-h-0 w-full",
            "max-w-4xl mx-auto xl:max-w-none xl:mx-0"
          )}
        >
          <div className="flex flex-col flex-1 min-h-0 rounded-2xl border border-primary/20 bg-dark-card shadow-card overflow-hidden">
            {/* Conversation toolbar */}
            <div className="flex items-center justify-between gap-3 p-4 border-b border-primary/20 bg-dark-slate/80 flex-shrink-0">
              <div className="flex items-center gap-3 min-w-0">
                <Avatar className="w-10 h-10 ring-2 ring-primary/30 flex-shrink-0">
                  {trainer?.avatarUrl ? (
                    <AvatarImage src={trainer.avatarUrl} alt={trainer.name} />
                  ) : (
                    <AvatarFallback className="bg-dark-card text-white font-semibold">
                      {trainer?.initials || "TR"}
                    </AvatarFallback>
                  )}
                </Avatar>
                <div className="min-w-0">
                  <div className="font-semibold text-white leading-tight truncate">
                    {trainer?.name || "Your Trainer"}
                  </div>
                  {trainerId && (
                    <p className="text-xs text-gray-light truncate">Your assigned trainer</p>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-1 flex-shrink-0">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onRefresh}
                  disabled={refreshing || !trainerId}
                  className="text-primary hover:bg-primary/10 h-9 w-9"
                  title="Refresh messages"
                  aria-label="Refresh messages"
                >
                  <RefreshCw className={cn("w-4 h-4", refreshing && "animate-spin")} />
                </Button>
                <Button
                  asChild
                  variant="outline"
                  size="sm"
                  className="hidden sm:inline-flex border-primary/30 text-primary hover:bg-primary hover:text-black xl:hidden"
                >
                  <Link to="/client-profile#trainer">View profile</Link>
                </Button>
              </div>
            </div>

            <ChatMessageList
              ref={listRef}
              messages={messages}
              loading={loading}
              trainerResolved={trainerResolved}
              trainerId={trainerId}
            />

            <ChatComposer
              draft={draft}
              onDraftChange={onDraftChange}
              onSend={onSend}
              disabled={composerDisabled}
              sending={sending}
            />
          </div>
        </div>

        <TrainerChatSidebar
          trainer={trainer}
          trainerId={trainerId}
          trainerResolved={trainerResolved}
        />
      </div>
    </div>
  );
}
